// Turns one sensor's day of readings into plain statements for InsightCards.
//
// Every number here is derived from the SAME array the chart draws, so the
// aside can never contradict the curve beside it — the invariant the FCR
// insights in Dashboard.js were built on.
//
// Two things this deliberately does not do:
//   * It never claims a percentage of TIME. Readings arrive every 5-15 minutes
//     and the cadence is not guaranteed even (and a barn with two sub-probes
//     returns interleaved rows), so "% of readings" is the only honest framing.
//   * It never re-formats a timestamp for display. `createdDate` is used for
//     DIFFERENCES only; the x-axis labels come from the server-side `xData`,
//     which is already formatted in Asia/Singapore. Re-deriving them in the
//     browser would silently shift by the viewer's timezone.

// A threshold that is absent, null, or the literal string 'null' (which the
// dashboard genuinely sends for coMin/nhMin) means "no bound on this side".
// Note Number(null) === 0, so a bare Number.isFinite() check would invent a
// hard floor at zero instead.
export const bound = (v) => {
  if (v === null || v === undefined || v === '' || v === 'null') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const median = (arr) => {
  if (!arr.length) return null;
  const s = [...arr].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

const mean = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);

// Which side of the band a value falls on. `null` bounds are open.
const sideOf = (v, lo, hi) => {
  if (hi !== null && v > hi) return 'high';
  if (lo !== null && v < lo) return 'low';
  return 'in';
};

// Elapsed minutes across a run of samples. Each gap is capped at 2x the median
// cadence so a four-hour gateway outage does not get reported as a four-hour
// excursion.
const spanMinutes = (times, from, to, capMs) => {
  if (!times || times[from] == null || times[to] == null) return null;
  let ms = 0;
  for (let i = from; i < to; i++) {
    const a = times[i], b = times[i + 1];
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
    ms += Math.min(Math.max(b - a, 0), capMs);
  }
  return ms / 60000;
};

const fmtDuration = (t, mins) => {
  if (mins == null || !Number.isFinite(mins)) return null;
  const m = Math.round(mins);
  if (m < 60) return t('dashboard:sensors.insights.minutes', { mins: m });
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0
    ? t('dashboard:sensors.insights.hours', { hrs: h })
    : t('dashboard:sensors.insights.hoursMinutes', { hours: h, minutes: rem });
};

/**
 * @param values      finite readings, in chronological order
 * @param timestamps  epoch ms aligned with `values` (may be empty)
 * @param lo,hi       already passed through bound(); either may be null
 * @param deadband    below this, a change counts as "steady" (readings are 1dp)
 * @param num         (value, decimals) => localised string
 */
export function buildSensorInsights({
  t, values, timestamps, lo, hi, unit = '', decimals = 1, deadband = 0.3, num,
}) {
  const vals = (values || []).filter((v) => Number.isFinite(v));
  const n = vals.length;
  if (n === 0) return [];

  const u = unit || '';
  const fmt = (v) => `${num(v, decimals)}${u}`;
  const cards = [];

  // Too little to say anything defensible. One honest card beats four guesses.
  if (n < 6) {
    return [{
      key: 'few',
      tone: 'info',
      icon: 'hourglass_empty',
      title: t('dashboard:sensors.insights.fewTitle', { n }),
      body: t('dashboard:sensors.insights.fewBody'),
    }];
  }

  const hasBand = lo !== null || hi !== null;
  const sides = hasBand ? vals.map((v) => sideOf(v, lo, hi)) : null;

  const bandLabel = lo !== null && hi !== null
    ? `${fmt(lo)}–${fmt(hi)}`
    : hi !== null
      ? t('dashboard:sensors.insights.below', { value: fmt(hi) })
      : t('dashboard:sensors.insights.above', { value: fmt(lo) });

  // 1. How much of the day sat inside the band.
  if (hasBand) {
    const inCount = sides.filter((s) => s === 'in').length;
    // floor, not round: 99.6% must not be published as "100% in range" on a day
    // that actually breached. That lie is the exact thing this card exists to
    // prevent.
    const pct = Math.floor((inCount / n) * 100);
    cards.push({
      key: 'inrange',
      tone: pct >= 95 ? 'good' : pct >= 80 ? 'info' : 'bad',
      icon: pct >= 95 ? 'check_circle' : pct >= 80 ? 'info' : 'error_outline',
      title: t('dashboard:sensors.insights.inRangeTitle', { pct: num(pct, 0) }),
      body: t('dashboard:sensors.insights.inRangeBody', {
        inCount: num(inCount, 0), total: num(n, 0), band: bandLabel,
      }),
    });
  }

  // 2. Longest unbroken excursion, reported as elapsed time where the
  //    timestamps allow it and as a reading count where they do not.
  if (hasBand) {
    const gaps = [];
    for (let i = 1; i < (timestamps || []).length; i++) {
      const d = timestamps[i] - timestamps[i - 1];
      if (Number.isFinite(d) && d > 0) gaps.push(d);
    }
    const medGap = median(gaps);
    const capMs = medGap ? medGap * 2 : null;

    let best = null, runStart = -1;
    for (let i = 0; i <= n; i++) {
      const out = i < n && sides[i] !== 'in';
      if (out && runStart === -1) runStart = i;
      if (!out && runStart !== -1) {
        const len = i - runStart;
        if (!best || len > best.len) best = { len, from: runStart, to: i - 1, side: sides[runStart] };
        runStart = -1;
      }
    }
    if (best && best.len >= 2) {
      const mins = capMs ? spanMinutes(timestamps, best.from, best.to, capMs) : null;
      const duration = fmtDuration(t, mins);
      cards.push({
        key: 'spell',
        tone: 'bad',
        icon: 'timelapse',
        title: duration
          ? t('dashboard:sensors.insights.spellTitle', { duration })
          : t('dashboard:sensors.insights.spellTitleReadings', { n: best.len }),
        body: t('dashboard:sensors.insights.spellBody', {
          n: num(best.len, 0),
          direction: best.side === 'high'
            ? t('dashboard:sensors.insights.aboveWord')
            : t('dashboard:sensors.insights.belowWord'),
        }),
      });
    }
  }

  // 3. Which way it is moving right now. Tone stays neutral: a direction is
  //    neither good nor bad without knowing which side of the band you are on.
  const recent = mean(vals.slice(-3));
  const prior = mean(vals.slice(-6, -3));
  if (recent !== null && prior !== null) {
    const delta = recent - prior;
    const steady = Math.abs(delta) < deadband;
    cards.push({
      key: 'direction',
      tone: 'neutral',
      icon: steady ? 'trending_flat' : delta > 0 ? 'trending_up' : 'trending_down',
      title: steady
        ? t('dashboard:sensors.insights.steadyTitle')
        : t(delta > 0 ? 'dashboard:sensors.insights.risingTitle'
                      : 'dashboard:sensors.insights.fallingTitle',
            { delta: fmt(Math.abs(delta)) }),
      body: t('dashboard:sensors.insights.directionBody', {
        recent: fmt(recent), prior: fmt(prior),
      }),
    });
  }

  // 4. Today's spread.
  const dMin = Math.min(...vals);
  const dMax = Math.max(...vals);
  const swing = dMax - dMin;
  cards.push({
    key: 'swing',
    tone: swing < deadband ? 'good' : 'neutral',
    icon: swing < deadband ? 'horizontal_rule' : 'swap_vert',
    title: swing < deadband
      ? t('dashboard:sensors.insights.flatTitle')
      : t('dashboard:sensors.insights.swingTitle', { delta: fmt(swing) }),
    body: t('dashboard:sensors.insights.swingBody', {
      min: fmt(dMin), max: fmt(dMax), n: num(n, 0),
    }),
  });

  return cards;
}
